import React from "react";
import { Droppable, Draggable } from "react-beautiful-dnd";
import DraggableMissions from "../DraggableMissions";

const sectionVisibilityChecks = {
  summary: (sections) => sections?.summary?.content,
  experience: (sections) => sections?.experience?.items?.length > 0 && sections.experience.items[0]?.position,
  education: (sections) => sections?.education?.items?.length > 0 && sections.education.items[0]?.institution,
  certifications: (sections) => sections?.certifications?.items?.length > 0 && sections.certifications.items[0]?.name,
  projects: (sections) => sections?.projects?.items?.length > 0 && sections.projects.items[0]?.name,
  references: (sections) => sections?.references?.items?.length > 0 && sections.references.items[0]?.name,
  languages: (sections) => sections?.languages?.items?.length > 0 && sections.languages.items[0]?.name,
};

const spacingMap = {
  compact: { gap: 14, padding: 24, itemGap: 10 },
  comfortable: { gap: 18, padding: 28, itemGap: 12 },
  spacious: { gap: 22, padding: 32, itemGap: 14 },
};

const sectionTitleStyle = (config) => {
  const base = {
    margin: 0,
    marginBottom: 12,
    color: config.palette.primary,
    fontSize: 15,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  };

  if (config.titleAlign === "center") {
    base.textAlign = "center";
  }

  switch (config.titleVariant) {
    case "block":
      return {
        ...base,
        display: "inline-block",
        color: "#fff",
        background: config.palette.primary,
        padding: "6px 12px",
        borderRadius: 8,
      };
    case "underline":
      return {
        ...base,
        paddingBottom: 6,
        borderBottom: `3px solid ${config.palette.accent}`,
      };
    case "capsule":
      return {
        ...base,
        display: "inline-block",
        background: config.palette.surface,
        padding: "6px 12px",
        borderRadius: 999,
        border: `1px solid ${config.palette.accent}`,
      };
    case "minimal":
      return {
        ...base,
        color: config.palette.secondary,
        letterSpacing: "0.14em",
      };
    default:
      return {
        ...base,
        paddingLeft: 10,
        borderLeft: `4px solid ${config.palette.accent}`,
      };
  }
};

const sectionShellStyle = (config) => {
  if (config.sectionSurface === "soft") {
    return {
      padding: "14px 16px",
      borderRadius: 16,
      background: config.palette.surface,
    };
  }

  if (config.sectionSurface === "outlined") {
    return {
      padding: "14px 16px",
      borderRadius: 16,
      border: `1px solid ${config.palette.accent}`,
      background: "#fff",
    };
  }

  return {
    paddingBottom: 2,
  };
};

const contactRowStyle = (config) => {
  if (config.contactLayout === "centered") {
    return {
      justifyContent: "center",
      textAlign: "center",
    };
  }

  if (config.contactLayout === "stacked") {
    return {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 6,
    };
  }

  if (config.contactLayout === "split") {
    return {
      justifyContent: "space-between",
    };
  }

  return {};
};

const renderSkills = ({ sections, skills, downloadable, config }) => {
  if (!(sections?.skills?.items?.length > 0 && sections.skills.items[0]?.name)) {
    return null;
  }

  const listStyles = {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    padding: 0,
    margin: 0,
    listStyle: "none",
  };

  const itemStyle = (index) => {
    const darkSidebar = config.sidebarTone === "dark";

    if (config.skillVariant === "bars") {
      return {
        flex: "1 1 100%",
        padding: "8px 12px",
        borderRadius: 8,
        background: darkSidebar
          ? "rgba(255,255,255,0.08)"
          : `linear-gradient(90deg, ${config.palette.surface}, ${index % 2 === 0 ? "#ffffff" : config.palette.surface})`,
        borderLeft: `4px solid ${config.palette.accent}`,
        color: darkSidebar ? "#fff" : config.palette.text,
      };
    }

    if (config.skillVariant === "grid") {
      return {
        flex: "1 1 calc(50% - 8px)",
        minWidth: 120,
        padding: "10px 12px",
        borderRadius: 10,
        background: darkSidebar ? "rgba(255,255,255,0.08)" : config.palette.surface,
        border: `1px solid ${config.palette.accent}`,
        color: darkSidebar ? "#fff" : config.palette.text,
      };
    }

    if (config.skillVariant === "plain") {
      return {
        flex: "1 1 100%",
        padding: "4px 0",
        borderBottom: `1px dashed ${config.palette.accent}`,
        color: darkSidebar ? "#fff" : config.palette.text,
      };
    }

    if (config.skillVariant === "outline") {
      return {
        padding: "7px 12px",
        borderRadius: 999,
        border: `1px solid ${darkSidebar ? "rgba(255,255,255,0.5)" : config.palette.secondary}`,
        background: darkSidebar ? "transparent" : "#fff",
        color: darkSidebar ? "#fff" : config.palette.text,
      };
    }

    return {
      padding: "7px 12px",
      borderRadius: 999,
      background: darkSidebar ? "rgba(255,255,255,0.08)" : config.palette.surface,
      color: darkSidebar ? "#fff" : config.palette.primary,
      border: `1px solid ${config.palette.accent}`,
    };
  };

  const content = downloadable ? (
    <ul style={listStyles}>
      {skills.list2.map((skill, index) => (
        <li key={`${skill}-${index}`} style={itemStyle(index)}>
          {skill}
        </li>
      ))}
    </ul>
  ) : (
    <Droppable droppableId="list2" direction={config.skillVariant === "bars" || config.skillVariant === "plain" ? "vertical" : "horizontal"}>
      {(provided) => (
        <ul
          {...provided.droppableProps}
          ref={provided.innerRef}
          style={listStyles}
        >
          {skills.list2.map((skill, index) => (
            <Draggable key={skill} draggableId={`list2-${skill}`} index={index}>
              {(dragProvided) => (
                <li
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  {...dragProvided.dragHandleProps}
                  style={itemStyle(index)}
                >
                  {skill}
                </li>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </ul>
      )}
    </Droppable>
  );

  return {
    id: "skills",
    title: sections.skills.name,
    content,
  };
};

const renderSidebarBlocks = ({ sections, config }) => {
  const blocks = [];

  if (sectionVisibilityChecks.languages(sections)) {
    blocks.push({
      id: "languages",
      title: sections.languages.name,
      content: (
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {sections.languages.items.map((language, index) => (
            <li key={`${language.name}-${index}`} style={{ marginBottom: 6 }}>
              {language.name}
            </li>
          ))}
        </ul>
      ),
    });
  }

  if (sectionVisibilityChecks.references(sections)) {
    blocks.push({
      id: "references",
      title: sections.references.name,
      content: (
        <div style={{ display: "grid", gap: 10 }}>
          {sections.references.items.map((reference, index) => (
            <div
              key={`${reference.name}-${index}`}
              style={{
                padding: 10,
                borderRadius: 10,
                background: "#fff",
                border: `1px solid ${config.palette.accent}`,
              }}
            >
              <div style={{ fontWeight: 700 }}>{reference.name}</div>
              <div style={{ fontSize: 12 }}>{reference.email}</div>
              <div style={{ fontSize: 12 }}>{reference.phone}</div>
            </div>
          ))}
        </div>
      ),
    });
  }

  return blocks;
};

const renderMainSection = ({ id, sections, config, downloadable }) => {
  switch (id) {
    case "summary":
      return {
        id,
        title: sections.summary.name,
        content: <p style={{ margin: 0, lineHeight: 1.7 }}>{sections.summary.content}</p>,
      };
    case "experience":
      return {
        id,
        title: sections.experience.name,
        content: (
          <div style={{ display: "grid", gap: 14 }}>
            {sections.experience.items.map((experience, index) => (
              <div
                key={`${experience.position}-${index}`}
                style={{
                  padding: config.experienceStyle === "cards" ? "14px 16px" : config.experienceStyle === "timeline" ? "4px 0 10px 20px" : "0 0 12px",
                  borderBottom: config.experienceStyle === "minimal"
                    ? `1px solid ${config.palette.surface}`
                    : "none",
                  borderLeft: config.experienceStyle === "timeline"
                    ? `3px solid ${config.palette.accent}`
                    : "none",
                  borderRadius: config.experienceStyle === "cards" ? 14 : 0,
                  background: config.experienceStyle === "cards" ? config.palette.surface : "transparent",
                  position: "relative",
                }}
              >
                {config.experienceStyle === "timeline" && (
                  <span
                    style={{
                      position: "absolute",
                      left: -8,
                      top: 8,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: config.palette.accent,
                    }}
                  />
                )}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{experience.position}</div>
                    <div style={{ color: config.palette.secondary }}>
                      {experience.company} | {experience.location}
                    </div>
                  </div>
                  <div style={{ color: config.palette.secondary, fontSize: 13 }}>{experience.date}</div>
                </div>
                <div style={{ marginTop: 10 }}>
                  {downloadable ? (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {(experience.missions || []).map((mission, missionIndex) => (
                        <li key={`${experience.position}-${missionIndex}`} style={{ marginBottom: 6 }}>
                          {mission}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <DraggableMissions droppableId={`list4-${index}`} experience={experience} />
                  )}
                </div>
              </div>
            ))}
          </div>
        ),
      };
    case "education":
      return {
        id,
        title: sections.education.name,
        content: (
          <div style={{ display: "grid", gap: 12 }}>
            {sections.education.items.map((education, index) => (
              <div
                key={`${education.institution}-${index}`}
                style={{
                  padding: "10px 12px",
                  borderRadius: 10,
                  background: config.palette.surface,
                }}
              >
                <div style={{ fontWeight: 700 }}>{education.studyType}</div>
                <div>{education.institution}</div>
                <div style={{ fontSize: 13, color: config.palette.secondary }}>{education.date}</div>
              </div>
            ))}
          </div>
        ),
      };
    case "certifications":
      return {
        id,
        title: sections.certifications.name,
        content: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {sections.certifications.items.map((certification, index) => (
              <li key={`${certification.name}-${index}`} style={{ marginBottom: 8 }}>
                {certification.name}
              </li>
            ))}
          </ul>
        ),
      };
    case "projects":
      return {
        id,
        title: sections.projects.name,
        content: (
          <div style={{ display: "grid", gap: 12 }}>
            {sections.projects.items.map((project, index) => (
              <div
                key={`${project.name}-${index}`}
                style={config.projectStyle === "cards" ? {
                  padding: "12px 14px",
                  borderRadius: 14,
                  background: config.palette.surface,
                  border: `1px solid ${config.palette.accent}`,
                } : undefined}
              >
                <div style={{ fontWeight: 700 }}>{project.name}</div>
                <div style={{ lineHeight: 1.6 }}>{project.description}</div>
              </div>
            ))}
          </div>
        ),
      };
    case "references":
      return {
        id,
        title: sections.references.name,
        content: (
          <div style={{ display: "grid", gap: 12 }}>
            {sections.references.items.map((reference, index) => (
              <div key={`${reference.name}-${index}`}>
                <div style={{ fontWeight: 700 }}>{reference.name}</div>
                <div>{reference.email}</div>
                <div>{reference.phone}</div>
              </div>
            ))}
          </div>
        ),
      };
    default:
      return null;
  }
};

const headerStyleMap = (config) => {
  const common = {
    background: config.headerVariant === "highlight"
      ? `linear-gradient(135deg, ${config.palette.primary}, ${config.palette.secondary})`
      : config.palette.primary,
    color: "#fff",
    borderRadius: 18,
    padding: "22px 24px",
  };

  if (config.headerVariant === "centered") {
    return { ...common, textAlign: "center" };
  }

  if (config.headerVariant === "split") {
    return { ...common, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" };
  }

  if (config.headerVariant === "stacked") {
    return { ...common, borderBottomRightRadius: 42 };
  }

  if (config.headerVariant === "highlight") {
    return { ...common, boxShadow: `0 16px 30px ${config.palette.surface}` };
  }

  return common;
};

const DynamicTemplate = ({ data, skills, downloadable, config }) => {
  const { basics = {}, sections = {} } = data;
  const spacing = spacingMap[config.density];
  const sectionIds = config.sectionOrder.filter((sectionId) => sectionVisibilityChecks[sectionId]?.(sections));
  const sidebarBlocks = [
    renderSkills({ sections, skills, downloadable, config }),
    ...renderSidebarBlocks({ sections, config }),
  ].filter(Boolean);
  const mainBlocks = sectionIds
    .map((sectionId) => renderMainSection({ id: sectionId, sections, config, downloadable }))
    .filter(Boolean);
  const hasSidebar = config.sidebarVariant !== "none" && sidebarBlocks.length > 0;
  const sidebarFirst = config.sidebarVariant === "left";

  const sidebar = hasSidebar ? (
    <aside
      style={{
        width: "34%",
        minWidth: 220,
        background: config.sidebarTone === "dark" ? config.palette.primary : config.palette.surface,
        borderRadius: 18,
        padding: 20,
        display: "grid",
        gap: 18,
        color: config.sidebarTone === "dark" ? "#fff" : config.palette.text,
        boxShadow: config.sidebarTone === "tinted" ? `inset 0 0 0 1px ${config.palette.accent}` : "none",
      }}
    >
      {sidebarBlocks.map((block) => (
        <section key={block.id}>
          <h3
            style={{
              ...sectionTitleStyle(config),
              color: config.sidebarTone === "dark" ? "#fff" : sectionTitleStyle(config).color,
            }}
          >
            {block.title}
          </h3>
          {block.content}
        </section>
      ))}
    </aside>
  ) : null;

  return (
    <div
      style={{
        width: "100%",
        padding: 18,
        background: "#f4f7fb",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 900,
          minHeight: 520,
          margin: "0 auto",
          background: "#ffffff",
          color: config.palette.text,
          fontFamily: config.fontFamily,
          fontSize: 13.4,
          lineHeight: 1.55,
          boxSizing: "border-box",
          padding: spacing.padding,
          borderRadius: 8,
          boxShadow: downloadable ? "none" : "0 10px 28px rgba(15, 23, 42, 0.08)",
        }}
      >
        <header style={headerStyleMap(config)}>
          <div>
            <h1 style={{ margin: 0, fontSize: 30, lineHeight: 1.15 }}>{basics.name}</h1>
            {basics.label && <div style={{ marginTop: 6, opacity: 0.92, fontSize: 14 }}>{basics.label}</div>}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            {basics.email && <div>{basics.email}</div>}
            {basics.phone && <div>{basics.phone}</div>}
            {basics.location && <div>{basics.location}</div>}
          </div>
        </header>

        <div
          style={{
            marginTop: 10,
            paddingBottom: 14,
            borderBottom: `1px solid ${config.palette.surface}`,
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            color: config.palette.secondary,
            fontSize: 12.5,
            ...contactRowStyle(config),
          }}
        >
          {basics.linkedin && <div>{basics.linkedin}</div>}
          {basics.website && <div>{basics.website}</div>}
          {!basics.linkedin && !basics.website && basics.summary && (
            <div style={{ maxWidth: "100%" }}>{basics.summary}</div>
          )}
        </div>

        <div
          style={{
            marginTop: spacing.gap,
            display: hasSidebar ? "flex" : "block",
            flexDirection: sidebarFirst ? "row" : "row-reverse",
            gap: spacing.gap,
            alignItems: "flex-start",
          }}
        >
          {sidebar}
          <main
            style={{
              flex: 1,
              display: "grid",
              gap: spacing.gap,
              gridTemplateColumns: !hasSidebar && config.mainColumns === 2 ? "repeat(2, minmax(0, 1fr))" : "1fr",
              alignItems: "start",
            }}
          >
            {!hasSidebar && sidebarBlocks.length > 0 && sidebarBlocks.map((block) => (
              <section
                key={block.id}
                style={sectionShellStyle(config)}
              >
                <h3 style={sectionTitleStyle(config)}>{block.title}</h3>
                {block.content}
              </section>
            ))}
            {mainBlocks.map((block) => (
              <section
                key={block.id}
                style={sectionShellStyle(config)}
              >
                <h3 style={sectionTitleStyle(config)}>{block.title}</h3>
                {block.content}
              </section>
            ))}
          </main>
        </div>
      </div>
    </div>
  );
};

export default DynamicTemplate;
